// TTSManager.ts
'use client';
import { KokoroTTS } from './KokoroTTS';
import { BackendTTS } from './BackendTTS';
import { NativeTTS } from './NativeTTS';
import type { ITTSEngine, TTSOptions, TTSCapabilities } from './types';

export class TTSManager {
  private engines: ITTSEngine[] = [];
  private activeEngine: ITTSEngine | null = null;
  private fallbackIndex = 0;

  constructor() {
    // Priority order: Local > Backend > Native
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIPad = /iPad/.test(navigator.userAgent);
    const isIPhone = /iPhone/.test(navigator.userAgent);

    if (isSafari || isIPad || isIPhone) {
      // iPad/iPhone/Safari sequence: NativeTTS -> BackendTTS
      this.engines = [
        new NativeTTS(), // Web Speech API
        new BackendTTS(), // API + cache implementation
        new KokoroTTS(), // Your current GlobalTTS refactored
      ];
    } else {
      // Default sequence: KokoroTTS -> BackendTTS -> NativeTTS
      this.engines = [
        new KokoroTTS(), // Your current GlobalTTS refactored
        new BackendTTS(), // API + cache implementation
        new NativeTTS(), // Web Speech API
      ];
    }
  }

  async initialize(): Promise<void> {
    // Try engines in order until one works
    for (let i = 0; i < this.engines.length; i++) {
      const engine = this.engines[i];
      try {
        if (engine.isAvailable()) {
          await engine.initialize();
          this.activeEngine = engine;
          this.fallbackIndex = i;
          console.log(`TTS using: ${engine.GetEngineName()}`);
          return;
        }
      } catch (err) {
        console.warn(`${engine.constructor.name} failed:`, err);
        continue;
      }
    }
    throw new Error('No TTS engine available');
  }

  async speak(options: TTSOptions): Promise<void> {
    if (!this.activeEngine) throw new Error('TTS not initialized');

    // Check text length against capabilities
    const caps = this.activeEngine.getCapabilities();
    // console.log(
    //   'debug: TTSManager speak print the options.text.length and caps.maxTextLength:',
    //   options.text.length,
    //   caps.maxTextLength,
    // );
    if (options.text.length > caps.maxTextLength) {
      // Try fallback instead of alerting
      if (this.fallbackIndex < this.engines.length - 1) {
        console.log('Text too long for current engine, trying fallback');
        this.fallbackIndex++;
        this.activeEngine = this.engines[this.fallbackIndex];
        return this.speak(options);
      }
      throw new Error(`Text too long (max ${caps.maxTextLength} chars)`);
    }

    console.log('debug: TTSManager speak options:', options);
    return this.activeEngine.speak(options);
  }

  stop(): void {
    this.activeEngine?.stop();
  }

  pause(): void {
    this.activeEngine?.pause();
  }

  resume(): void {
    this.activeEngine?.resume();
  }

  getCapabilities(): TTSCapabilities | null {
    return this.activeEngine?.getCapabilities() || null;
  }

  destroy(): void {
    // TTSManager.ts
    this.engines.forEach((e) => {
      if ('destroy' in e && typeof e.destroy === 'function') {
        e.destroy();
      }
    });
  }
}
